import { useEffect, useRef } from "react";
import { supabase, SUPABASE_REINIT_EVENT } from "@/lib/supabase";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import {
  useRealtimeConnectionControls,
  type ChannelStatus,
} from "@/contexts/RealtimeConnectionContext";

export interface UseRealtimeRefreshOptions {
  debounceMs?: number;
  enabled?: boolean;
  /**
   * When true, realtime payloads that originated from our own optimistic edits
   * (marked via markSelfEdit) do not trigger the onChange refetch. Use this when
   * the consumer already reflects its own writes locally (optimistic update or an
   * explicit refresh), so the echoed refetch would only cause a redundant
   * full re-render. External changes still refetch as usual.
   */
  skipRefetchOnSelfEdit?: boolean;
}

const DEFAULT_DEBOUNCE_MS = 300;

type RealtimePayload = {
  table?: string;
  new?: Record<string, unknown> | null;
  old?: Record<string, unknown> | null;
};

function pickId(row: Record<string, unknown> | null | undefined, key: string = "id"): string | null {
  const v = row?.[key];
  return typeof v === "string" ? v : null;
}

/**
 * Subscribes to Supabase Realtime postgres_changes for the given tables and invokes
 * a debounced onChange callback whenever any payload arrives.
 *
 * - One channel per hook instance, one listener per table
 * - Stable onChange via ref so identity changes don't tear down the subscription
 * - Rebuilds on SUPABASE_REINIT_EVENT
 * - Publishes channel status into RealtimeConnectionContext
 * - For each payload: marks (table, id) as recently changed unless it's in the self-edit ignore-set;
 *   for assignment_employees, also marks the parent project_assignments row
 */
export function useRealtimeRefresh(
  tables: string[],
  onChange: () => void,
  options?: UseRealtimeRefreshOptions
): void {
  const debounceMs = options?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const enabled = options?.enabled ?? true;
  const skipRefetchOnSelfEdit = options?.skipRefetchOnSelfEdit ?? false;

  const skipRefetchOnSelfEditRef = useRef(skipRefetchOnSelfEdit);
  useEffect(() => {
    skipRefetchOnSelfEditRef.current = skipRefetchOnSelfEdit;
  }, [skipRefetchOnSelfEdit]);

  // Stable refs so subscription doesn't tear down on every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const { markChanged, isSelfEdit } = useRecentlyChangedRowsApi();
  const markChangedRef = useRef(markChanged);
  const isSelfEditRef = useRef(isSelfEdit);
  useEffect(() => {
    markChangedRef.current = markChanged;
    isSelfEditRef.current = isSelfEdit;
  }, [markChanged, isSelfEdit]);

  const { setChannelStatus, removeChannel: removeChannelFromCtx } = useRealtimeConnectionControls();
  const setChannelStatusRef = useRef(setChannelStatus);
  const removeChannelFromCtxRef = useRef(removeChannelFromCtx);
  useEffect(() => {
    setChannelStatusRef.current = setChannelStatus;
    removeChannelFromCtxRef.current = removeChannelFromCtx;
  }, [setChannelStatus, removeChannelFromCtx]);

  // Channel key is stable for a given table list; sort to be order-insensitive.
  const tableListKey = [...tables].sort().join(",");

  // Per-instance UUID ensures each hook instance gets its own Supabase channel.
  // Supabase caches channels by name and locks listeners once `.subscribe()` is called,
  // so two hooks with the same table list must not share a channel.
  const instanceIdRef = useRef<string>(
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `r${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  );

  useEffect(() => {
    if (!enabled || tables.length === 0) return;

    const channelKey = `realtime:${instanceIdRef.current}:${tableListKey}`;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryAttempt = 0;
    // When the current outage began, so a recovery can report how long it took.
    let droppedAt: number | null = null;
    let disposed = false;
    // Bumped whenever we build or discard a channel, so a status callback from a
    // channel we have already replaced cannot schedule a rebuild for the live one.
    let generation = 0;

    const scheduleOnChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        try {
          onChangeRef.current();
        } catch (err) {
          console.debug("[useRealtimeRefresh] onChange threw:", err);
        }
      }, debounceMs);
    };

    const handlePayload = (table: string, payload: RealtimePayload) => {
      const newRow = (payload?.new ?? null) as Record<string, unknown> | null;
      const oldRow = (payload?.old ?? null) as Record<string, unknown> | null;
      const id = pickId(newRow) ?? pickId(oldRow);
      // Track whether this payload originated from our own optimistic edit. Such
      // changes are already reflected locally, so we skip the refetch to avoid a
      // visible full re-render/re-sort of the list.
      let isSelf = false;
      if (id) {
        if (isSelfEditRef.current(table, id)) {
          isSelf = true;
        } else {
          markChangedRef.current(table, id);
        }
      }
      // Special-case: assignment_employees changes should flash the parent assignment row.
      if (table === "assignment_employees") {
        const assignmentId = pickId(newRow, "assignment_id") ?? pickId(oldRow, "assignment_id");
        if (assignmentId) {
          if (isSelfEditRef.current("project_assignments", assignmentId)) {
            isSelf = true;
          } else {
            markChangedRef.current("project_assignments", assignmentId);
          }
        }
      }
      if (!(isSelf && skipRefetchOnSelfEditRef.current)) {
        scheduleOnChange();
      }
    };

    const discardChannel = () => {
      generation += 1;
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }
    };

    /**
     * Rebuild the channel after the server drops it.
     *
     * A channel that receives a close removes itself from the socket
     * (`Channel.onClose` -> `socket.remove`), so nothing rejoins it: the socket
     * itself is usually still healthy and has no reason to reconnect. Without
     * this the subscription stays dead until the component remounts, which in a
     * client-routed SPA means the status chip reads "Reconnecting…" indefinitely
     * while nothing is actually retrying.
     *
     * Backoff caps at 30s. Timers are throttled hard in a hidden tab, so a
     * backgrounded page recovers on its own a little later rather than never.
     */
    const scheduleRebuild = () => {
      if (disposed || retryTimer) return;
      retryAttempt += 1;
      const delay = Math.min(1000 * 2 ** (retryAttempt - 1), 30_000);
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (disposed) return;
        discardChannel();
        channel = build();
      }, delay);
    };

    const build = () => {
      const buildGeneration = (generation += 1);
      setChannelStatusRef.current(channelKey, "JOINING");
      const ch = supabase.channel(channelKey);
      for (const table of tables) {
        // The supabase JS types for `.on('postgres_changes', ...)` are notoriously narrow;
        // cast args/handler to keep this generic across table names.
        (ch as unknown as {
          on: (
            type: "postgres_changes",
            filter: { event: "*"; schema: "public"; table: string },
            cb: (payload: RealtimePayload) => void
          ) => unknown;
        }).on(
          "postgres_changes",
          { event: "*", schema: "public", table },
          (payload: RealtimePayload) => handlePayload(table, payload)
        );
      }
      ch.subscribe((status: string) => {
        // Ignore callbacks from a channel we have already torn down or replaced —
        // notably the close that `removeChannel` itself triggers, which would
        // otherwise look like a server drop and queue a pointless rebuild.
        if (disposed || buildGeneration !== generation) return;

        const normalized = (status as ChannelStatus) ?? "CLOSED";
        setChannelStatusRef.current(channelKey, normalized);

        if (normalized === "SUBSCRIBED") {
          // Failures are logged below; log the recovery too, so a console
          // capture shows whether (and how quickly) the channel came back.
          if (retryAttempt > 0) {
            const downFor = droppedAt === null ? "?" : ((Date.now() - droppedAt) / 1000).toFixed(1);
            const attempts = `${retryAttempt} ${retryAttempt === 1 ? "attempt" : "attempts"}`;
            console.debug(
              `[useRealtimeRefresh] channel ${channelKey} recovered after ${attempts}, down ${downFor}s`
            );
          }
          retryAttempt = 0;
          droppedAt = null;
          if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
          }
          return;
        }

        if (
          normalized === "CHANNEL_ERROR" ||
          normalized === "TIMED_OUT" ||
          normalized === "CLOSED"
        ) {
          console.debug(`[useRealtimeRefresh] channel ${channelKey} status: ${normalized}`);
          droppedAt ??= Date.now();
          scheduleRebuild();
        }
      });
      return ch;
    };

    channel = build();

    const handleReinit = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      retryAttempt = 0;
      droppedAt = null;
      discardChannel();
      channel = build();
    };
    if (typeof window !== "undefined") {
      window.addEventListener(SUPABASE_REINIT_EVENT, handleReinit);
    }

    return () => {
      // Set first: removeChannel below triggers a close, and the status callback
      // must not treat our own teardown as a drop worth retrying.
      disposed = true;
      if (typeof window !== "undefined") {
        window.removeEventListener(SUPABASE_REINIT_EVENT, handleReinit);
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      if (retryTimer) clearTimeout(retryTimer);
      discardChannel();
      removeChannelFromCtxRef.current(channelKey);
    };
  }, [tableListKey, debounceMs, enabled]);
}
